import { clsx } from 'clsx';
import { useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { getTailwindColor } from '@/utils/colors';
import { getImageUrl } from '@/utils/images';
import { BlockBase } from '@/queries/page';
import { AppContext } from '@/contexts/app-context';
import { useLocalization } from '@/hooks/use-localization';
import { DynamicModuleGenerator } from '../../dynamic-module-generator/dynamic-module-generator';
import { VideoControls } from './video-controls';
import { useHlsPlayback } from '../streaming-video/use-hls-playback';

export type VideoSectionProps = {
    /**
     * HLS master playlist URL (e.g. `/automator/videos/.../master.m3u8?sign=…`).
     * The block's only video source: Safari uses the native
     * `<source type="…mpegurl">` path, everything else dynamically loads
     * hls.js. Editors pick a video attachment processed by Image Automator
     * 3000 and `block.php` resolves it to the signed master URL; absent or
     * empty means the section renders its poster (or nothing) instead.
     */
    masterUrl?: string | null;
    posterImage?: {
        src: string;
        width?: number;
        height?: number;
        __isimage?: boolean;
    } | null;
    /**
     * Vertical focus for the cropped video. The video is rendered with
     * `object-fit: cover`, so when the section's aspect ratio differs from
     * the source video, parts of the frame are clipped vertically.
     *
     * - `'top'` keeps the top of the frame visible (for footage with
     *   important content high in the frame).
     * - `'middle'` (default) — centred cover, browser default.
     * - `'bottom'` keeps the bottom of the frame visible (use this for
     *   footage with burned-in subtitles).
     *
     * Translated into CSS `object-position: center top | center | center bottom`.
     * We deliberately don't expose horizontal control: 99 % of the time the
     * subtitle / framing problem is vertical, and a simple three-option
     * choice is far less error-prone than a free-form X/Y picker.
     */
    focusPoint?: 'top' | 'middle' | 'bottom';
    autoplay?: boolean;
    loop?: boolean;
    overlayOpacity?: number;
    backgroundColor?: string;
    visibility?: 'both' | 'mobile' | 'desktop';
    /**
     * Height of the section as a percentage of the viewport height (20–100).
     * 100 is treated as a full-screen hero header and subtracts the sticky
     * header height so the video sits flush below it. Values < 100 are
     * mid-page sections. Defaults to 60 — a comfortable mid-page default.
     */
    sectionHeightVh?: number;
    textPosition?: 'left' | 'center' | 'right';
    /**
     * Vertical placement of the inner blocks within the section.
     */
    verticalAlignment?: 'top' | 'middle' | 'bottom';
    textAccessibilityDescription?: string;
    innerBlocks?: BlockBase[];
};

/**
 * Background video section with text overlay.
 *
 * Can be used both as a page header (sectionHeightVh === 100) and as a
 * mid-page section (any smaller vh value). Uses a native <video> element —
 * NOT an iframe or react-player — so we can:
 *   - layer text content on top of the video,
 *   - implement accessible, keyboard-friendly mute/pause controls,
 *   - honor `prefers-reduced-motion` properly.
 *
 * WCAG 2.2 coverage:
 *   - 1.2.1 (text alt) — `textAccessibilityDescription` rendered as visually-hidden
 *     text inside the region, picked up by screen readers.
 *   - 2.2.2 (pause/stop) — `<VideoControls>` is always visible.
 *   - 2.4.7 / 2.5.8 / 4.1.2 — covered in <VideoControls>.
 *   - reduced-motion — when user prefers reduced motion, we start PAUSED and
 *     render the poster image instead of auto-playing.
 */
export const VideoSection = ({
    masterUrl,
    posterImage,
    autoplay = true,
    loop = true,
    overlayOpacity = 0,
    backgroundColor = '',
    visibility = 'both',
    sectionHeightVh = 60,
    textPosition = 'left',
    verticalAlignment = 'middle',
    textAccessibilityDescription,
    focusPoint = 'middle',
    innerBlocks = [],
}: VideoSectionProps) => {
    const { deviceType } = useContext(AppContext);
    const { localize } = useLocalization();
    const videoRef = useRef<HTMLVideoElement | null>(null);

    // Device visibility. Computed up here because it gates the HLS hook
    // below: `r-hidden` only removes the pixels, so a hidden section would
    // otherwise still attach hls.js and buffer the whole video. With a
    // desktop/mobile section pair that means two simultaneous streams.
    const hidden = (visibility === 'desktop' && deviceType === 'mobile') || (visibility === 'mobile' && deviceType === 'desktop');

    // HLS playback path: when masterUrl is present, hand the <video> element
    // to the shared hook. It picks Safari-native HLS or dynamically imports
    // hls.js for everything else, plus handles ABR overrides + autoplay.
    // The hook is a no-op when masterUrl is empty/null, so this is always
    // safe to call (legacy MP4/WebM-only blocks pass through unaffected).
    useHlsPlayback(videoRef, { masterUrl: hidden ? undefined : masterUrl || undefined, autoplay });

    // prefers-reduced-motion: read once on mount + listen for changes. Matches the
    // pattern in carousel.tsx (line 61-70). When true, we do NOT autoplay.
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mq.matches);
        const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const shouldAutoplay = autoplay && !prefersReducedMotion;

    // Initial state MUST match what the server rendered to avoid hydration mismatch.
    // SSR can't know `prefersReducedMotion` yet, so we use the `autoplay` prop directly
    // for the initial values. The post-mount `useEffect` below syncs from the real
    // <video> element once it's playing/paused, and the reduced-motion effect pauses
    // it if needed.
    const [isPlaying, setIsPlaying] = useState(autoplay);
    const [isMuted, setIsMuted] = useState(true); // always start muted (browser autoplay policy)

    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;

        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onVolumeChange = () => setIsMuted(el.muted);

        el.addEventListener('play', onPlay);
        el.addEventListener('pause', onPause);
        el.addEventListener('volumechange', onVolumeChange);

        // Sync once on mount in case play/pause already happened before listener attach.
        setIsPlaying(!el.paused);
        setIsMuted(el.muted);

        return () => {
            el.removeEventListener('play', onPlay);
            el.removeEventListener('pause', onPause);
            el.removeEventListener('volumechange', onVolumeChange);
        };
    }, []);

    // React to reduced-motion changes after mount: if the user toggles the OS
    // setting mid-playback, pause the video. We don't auto-resume if they turn it
    // off again — that would be surprising; let them press play explicitly.
    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        if (prefersReducedMotion && !el.paused) {
            el.pause();
        }
    }, [prefersReducedMotion]);

    const togglePlay = useCallback(() => {
        const el = videoRef.current;
        if (!el) return;
        if (el.paused) {
            void el.play().catch(() => {
                // autoplay rejected — keep UI in paused state
                setIsPlaying(false);
            });
        } else {
            el.pause();
        }
    }, []);

    const toggleMute = useCallback(() => {
        const el = videoRef.current;
        if (!el) return;
        el.muted = !el.muted;
        setIsMuted(el.muted);
    }, []);

    // Clamp to supported range. `isHero` means the editor picked 100vh — the
    // block is being used as a full-screen page header rather than a mid-page
    // section. We treat these two cases slightly differently below (different
    // `--header-height` fallbacks), but both use the live CSS variable so the
    // real sticky-header height always wins after hydration.
    const clampedSectionVh = Math.max(20, Math.min(100, sectionHeightVh ?? 60));
    const isHero = clampedSectionVh === 100;

    // The section is a vertical flex column; `justify-content` places the
    // content (inner blocks container) along the section's min-height.
    // `middle` (the default) preserves the behaviour we shipped when we
    // introduced vertical centering; `top` / `bottom` let editors opt out.
    const containerClass = clsx('r-relative r-flex r-w-full r-flex-col r-overflow-hidden', getTailwindColor(backgroundColor, 'bg'), {
        'r-justify-start': verticalAlignment === 'top',
        'r-justify-center': verticalAlignment === 'middle',
        'r-justify-end': verticalAlignment === 'bottom',
        'r-hidden': hidden,
    });

    // Min-height rule is emitted via a scoped <style> tag rather than inline
    // `style={{...}}` or a Tailwind arbitrary value. Two reasons:
    //   1) Tailwind's JIT purger strips class names with runtime-computed
    //      numbers (the `sectionHeightVh` slider value).
    //   2) We need CSS `var(--header-height)` inside `calc()`, which Tailwind
    //      arbitrary values support poorly once a fallback is involved. A
    //      plain stylesheet rule sidesteps escaping issues.
    //
    // `--header-height` is set dynamically on document.documentElement by
    // <Header> via a ResizeObserver. It measures the entire sticky <header>
    // element (Alerts + MobileMenu OR TopMenu+MainMenu + optional InsightsNav),
    // so the same variable is correct at every breakpoint. Subtracting it
    // from the viewport-based height keeps the video flush with the bottom
    // of the sticky header instead of overflowing below the fold.
    //
    // Fallback (pre-hydration only, before the ResizeObserver has run):
    //   - hero (100vh): 140px — approximate Alerts + TopMenu + MainMenu
    //     stack on desktop. On mobile the real header is shorter (~64px)
    //     but the ResizeObserver kicks in before first paint in practice,
    //     so a single fallback value is acceptable and avoids a responsive
    //     media query for SSR.
    //   - section (<100vh): 0px — mid-page sections don't start at the
    //     viewport top, so subtraction isn't strictly needed; a 0px
    //     fallback keeps the pre-hydration paint stable.
    //
    // A unique id scopes the style rule so multiple video-sections with
    // different heights on the same page don't collide.
    const instanceId = useId();
    const instanceClass = `video-section-${instanceId.replace(/:/g, '')}`;

    const headerFallback = isHero ? 140 : 0;
    const minHeightRule = `min-height: calc(${clampedSectionVh}vh - var(--header-height, ${headerFallback}px));`;

    const textContainerClass = clsx(
        'r-relative r-z-20 r-mx-auto r-flex r-w-full r-max-w-[1200px] r-items-center r-px-4 r-py-16 md:r-px-8 md:r-py-24',
        {
            'r-justify-start r-text-left': textPosition === 'left',
            'r-justify-center r-text-center': textPosition === 'center',
            'r-justify-end r-text-right': textPosition === 'right',
        }
    );

    // Poster URL resolution. HLS posters come from IA3K (already absolute
    // /automator/images/... URLs from `block.php`) and shouldn't be passed
    // through `getImageUrl()` which would prepend `/image/{size}/...` —
    // those go through the rawb-images service. Detect HLS posters by
    // checking whether the URL is already absolute or starts with the
    // automator path; legacy posters go through `getImageUrl()` as before.
    const posterUrl = posterImage?.src
        ? masterUrl && (posterImage.src.startsWith('/automator/') || posterImage.src.startsWith('http'))
            ? posterImage.src
            : getImageUrl(posterImage.src, '1920xAUTO')
        : undefined;

    const hasMediaSource = Boolean(masterUrl);

    // A device-hidden section renders no media at all. `display: none` does
    // not stop a <video autoplay preload> from downloading its source, so
    // gating the element (not just the hook above) is what actually keeps
    // the hidden variant off the network.
    const renderVideo = !hidden && hasMediaSource;
    const renderPosterFallback = !hidden && !hasMediaSource && Boolean(posterUrl);

    // Translate the vertical focus enum into a CSS position string used both
    // by the <video>'s `object-position` and the fallback poster div's
    // `background-position`. `'middle'` falls back to "center" — CSS's normal
    // `cover` behaviour — so existing content snapshots that pre-date this
    // attribute keep rendering unchanged.
    const focusPositionStyle = focusPoint === 'top' ? 'center top' : focusPoint === 'bottom' ? 'center bottom' : 'center';
    const hasCustomFocus = focusPoint === 'top' || focusPoint === 'bottom';
    const videoStyle = hasCustomFocus ? { objectPosition: focusPositionStyle } : undefined;
    const posterFallbackStyle = hasCustomFocus
        ? { backgroundImage: `url(${posterUrl})`, backgroundPosition: focusPositionStyle }
        : { backgroundImage: `url(${posterUrl})` };

    return (
        <section
            data-component="video-section"
            className={clsx(containerClass, instanceClass)}
            aria-label={localize('video-region-label', true)}
            role="region"
        >
            {/* Visually-hidden text description of video content (WCAG 1.2.1) */}
            {textAccessibilityDescription && <p className="r-sr-only">{textAccessibilityDescription}</p>}

            {/*
                Scoped min-height — purge-safe (no dynamic Tailwind class),
                computed from the `sectionHeightVh` slider value. Subtracts
                the live `--header-height` CSS variable set by <Header> so
                the sticky top-menu + main-menu don't push content below
                the fold. Do NOT move this to inline style on the <section>
                — it would still work but defeats the per-instance scoping
                and the @media hook stays useful for future breakpoints.
            */}
            <style
                dangerouslySetInnerHTML={{
                    __html: `.${instanceClass} { ${minHeightRule} }`,
                }}
            />

            {/* Background video layer */}
            {renderVideo && (
                <video
                    ref={videoRef}
                    className="r-absolute r-inset-0 r-z-0 r-h-full r-w-full r-object-cover"
                    // Inline `object-position` only when a focus point is set,
                    // otherwise leave Tailwind's default `object-cover` (centred)
                    // untouched. Required for footage with burned-in subtitles
                    // or important framing near an edge.
                    style={videoStyle}
                    // Always muted at markup level for autoplay compliance; users unmute via control.
                    muted
                    playsInline
                    loop={loop}
                    autoPlay={shouldAutoplay}
                    preload={shouldAutoplay ? 'auto' : 'metadata'}
                    poster={posterUrl}
                    // Segment fetches need a proper Origin header for CORS
                    // via the automator ingress.
                    crossOrigin="anonymous"
                    // Hide default browser controls — we render our own accessible ones.
                    // aria-hidden because the <section> region + the sr-only description
                    // describe the media to AT; the <video> element itself is decorative here.
                    aria-hidden="true"
                    tabIndex={-1}
                >
                    {/*
                        A single <source type="…mpegurl"> handles Safari
                        natively. Other browsers can't decode HLS without
                        MSE, ignore this source, and rely on hls.js
                        attaching the MediaSource via useHlsPlayback above.
                    */}
                    <source src={masterUrl ?? ''} type="application/vnd.apple.mpegurl" />
                </video>
            )}

            {/* Fallback poster when no video is uploaded OR reduced-motion is on and there's no autoplay */}
            {renderPosterFallback && (
                <div
                    // `r-bg-center` is the default; we drop it from the class
                    // list when a focus point is set so our inline
                    // `background-position` wins (Tailwind's utility would tie
                    // on specificity but inline styles always win — this just
                    // removes the dead class for cleanliness).
                    className={clsx('r-absolute r-inset-0 r-z-0 r-bg-cover', !hasCustomFocus && 'r-bg-center')}
                    style={posterFallbackStyle}
                    aria-hidden="true"
                />
            )}

            {/* Dark overlay for text contrast (WCAG 1.4.3 / 1.4.11 support) */}
            {overlayOpacity > 0 && (
                <div
                    className="r-absolute r-inset-0 r-z-10 r-bg-black"
                    style={{ opacity: Math.min(Math.max(overlayOpacity, 0), 100) / 100 }}
                    aria-hidden="true"
                />
            )}

            {/* Text content overlay */}
            <div className={textContainerClass}>
                <div className="r-max-w-[640px] r-text-white">
                    <DynamicModuleGenerator blocks={innerBlocks} />
                </div>
            </div>

            {/* Accessible play/pause + mute/unmute controls (bottom-right) */}
            {renderVideo && (
                <VideoControls
                    isPlaying={isPlaying}
                    isMuted={isMuted}
                    onTogglePlay={togglePlay}
                    onToggleMute={toggleMute}
                    className="r-absolute r-bottom-4 r-right-4 r-z-30 md:r-bottom-6 md:r-right-6"
                />
            )}
        </section>
    );
};
